import React, { useEffect } from "react";
import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { searchFormSelector } from "redux/searchForm";
import { useNavigate } from "react-router-dom";
import { navigateDelay } from "pages/SearchResultsPage/animations.searchResultsPage";
import { buildQueryString } from "utils/queryStrings";
import { MediaQuery, SidebarLayout } from "@thekilroygroup/shared-ui";
import { LowpriceCalendarLink } from "../LowPriceCalendar/Navigation/LowpriceCalendarLink";
import SearchResults from "./SearchResults";
import DesktopFilters from "../Filters/DesktopFilters";

interface IProps {
  data: any;
  isFetching: boolean;
  pageIsMounted: boolean;
  setPageIsMounted: (value: boolean) => void;
}

export const SearchResultsContainer = (props: IProps) => {
  const theme = useTheme();
  const searchForm = useSelector(searchFormSelector);
  const {
    data,
    isFetching,
    pageIsMounted,
    setPageIsMounted = () => {},
  } = props;
  const navigate = useNavigate();

  useEffect(() => {
    if (isFetching) {
      window.scrollTo({
        behavior: "smooth",
        top: 0,
      });
    }
  }, [isFetching]);

  const confirmAndNavigateTo = (path: string) => {
    setPageIsMounted(false);
    setTimeout(() => {
      navigate(path);
    }, navigateDelay);
  };

  const navigateToLowpriceCalendar = () => {
    setPageIsMounted(false);
    setTimeout(() => {
      navigate(`/calendar?${buildQueryString(searchForm)}`);
    }, navigateDelay);
  };

  const searchResultsDelay = useMediaQuery(theme.breakpoints.up("md"))
    ? 500
    : 1;
  return (
    <SidebarLayout
      animated
      alignSidebar="left"
      mainContentAnimationProps={{
        in: pageIsMounted,
      }}
      sidebarAnimationProps={{
        in: pageIsMounted,
        delay: searchResultsDelay,
      }}
      sidebarSx={{
        backgroundColor: theme.palette.white.main,
      }}
      mainContent={
        <Box
          sx={{
            ...sxStyles.contentWrapper,
            transition: `width 350ms ${theme.transitions.easing.easeOut}`,
          }}
        >
          <SearchResults
            response={data}
            confirmAndNavigateTo={(path) => confirmAndNavigateTo(path)}
            setPageIsMounted={setPageIsMounted}
            pageIsMounted={pageIsMounted}
          />
        </Box>
      }
      sidebar={
        <MediaQuery min="md">
          <Box sx={{ ...sxStyles.sideBarInner }}>
            <Box sx={{ ...sxStyles.sideBarContent }}>
              <LowpriceCalendarLink
                handleNavigate={navigateToLowpriceCalendar}
                searchForm={searchForm}
                variant="contained"
                size="medium"
                sx={{
                  maxWidth: "300px",
                  mx: "auto",
                  width: "100%",
                  mt: 2,
                  mb: 37.5 / 8,
                }}
              />
              <DesktopFilters />
            </Box>
          </Box>
        </MediaQuery>
      }
    />
  );
};

const sxStyles: { [key: string]: SxProps } = {
  contentWrapper: {
    width: "100%",
    pt: { md: 3 },
    minHeight: { md: "90vh" },
  },

  sideBarInner: {
    backgroundColor: "white",
    p: { xs: 3, xxl: 3.75 },
  },
  sideBarContent: {
    maxWidth: "400px",
    marginLeft: "auto",
    width: {
      lg: "calc(1280px / 4 - 48px)",
      xxl: "100%",
    },
  },
};
